'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';
type Contrast = 'normal' | 'high';
type FontSize = 'default' | 'large' | 'x-large';
type Spacing = 'normal' | 'wide';

interface AccessibilityState {
  theme: Theme;
  contrast: Contrast;
  fontSize: FontSize;
  reducedMotion: boolean;
  spacing: Spacing;
  linkHighlight: boolean;
  readingGuide: boolean;
}

interface AccessibilityContextValue extends AccessibilityState {
  setTheme: (theme: Theme) => void;
  setContrast: (contrast: Contrast) => void;
  setFontSize: (fontSize: FontSize) => void;
  setReducedMotion: (reducedMotion: boolean) => void;
  setSpacing: (spacing: Spacing) => void;
  setLinkHighlight: (linkHighlight: boolean) => void;
  setReadingGuide: (readingGuide: boolean) => void;
  resetAll: () => void;
}

const defaults: AccessibilityState = {
  theme: 'light',
  contrast: 'normal',
  fontSize: 'default',
  reducedMotion: false,
  spacing: 'normal',
  linkHighlight: false,
  readingGuide: false,
};

const AccessibilityContext = createContext<AccessibilityContextValue | null>(null);

function applyThemeAttribute(theme: Theme) {
  const h = document.documentElement;
  if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    h.setAttribute('data-theme', 'dark');
  } else {
    h.removeAttribute('data-theme');
  }
}

function setOrRemove(attr: string, value: string | null) {
  const h = document.documentElement;
  if (value) {
    h.setAttribute(attr, value);
  } else {
    h.removeAttribute(attr);
  }
}

export function AccessibilityProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AccessibilityState>(() => {
    if (typeof window === 'undefined') return defaults;
    return {
      theme: (localStorage.getItem('a11y-theme') as Theme) || defaults.theme,
      contrast: (localStorage.getItem('a11y-contrast') as Contrast) || defaults.contrast,
      fontSize: (localStorage.getItem('a11y-font-size') as FontSize) || defaults.fontSize,
      reducedMotion: localStorage.getItem('a11y-reduced-motion') === 'true',
      spacing: (localStorage.getItem('a11y-spacing') as Spacing) || defaults.spacing,
      linkHighlight: localStorage.getItem('a11y-link-highlight') === 'true',
      readingGuide: localStorage.getItem('a11y-reading-guide') === 'true',
    };
  });

  // Sync theme with OS preference when set to 'system'
  useEffect(() => {
    if (state.theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyThemeAttribute('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [state.theme]);

  // Apply all attributes on state change
  useEffect(() => {
    applyThemeAttribute(state.theme);
    setOrRemove('data-contrast', state.contrast === 'high' ? 'high' : null);
    setOrRemove('data-font-size', state.fontSize !== 'default' ? state.fontSize : null);
    setOrRemove('data-spacing', state.spacing === 'wide' ? 'wide' : null);
    setOrRemove('data-reduced-motion', state.reducedMotion ? 'true' : null);
    setOrRemove('data-link-highlight', state.linkHighlight ? 'true' : null);
  }, [state]);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('a11y-theme', state.theme);
    localStorage.setItem('a11y-contrast', state.contrast);
    localStorage.setItem('a11y-font-size', state.fontSize);
    localStorage.setItem('a11y-reduced-motion', String(state.reducedMotion));
    localStorage.setItem('a11y-spacing', state.spacing);
    localStorage.setItem('a11y-link-highlight', String(state.linkHighlight));
    localStorage.setItem('a11y-reading-guide', String(state.readingGuide));
  }, [state]);

  // eMAG keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.altKey) return;

      switch (e.key) {
        case '1': {
          e.preventDefault();
          document.getElementById('main-content')?.focus();
          break;
        }
        case '2': {
          e.preventDefault();
          document.getElementById('main-nav')?.focus();
          break;
        }
        case '4': {
          e.preventDefault();
          document.getElementById('info-footer')?.focus();
          break;
        }
        case '5': {
          e.preventDefault();
          setState((prev) => {
            const cycle: FontSize[] = ['default', 'large', 'x-large'];
            const idx = cycle.indexOf(prev.fontSize);
            return { ...prev, fontSize: cycle[(idx + 1) % cycle.length] };
          });
          break;
        }
        case '6': {
          e.preventDefault();
          setState((prev) => ({
            ...prev,
            contrast: prev.contrast === 'normal' ? 'high' : 'normal',
          }));
          break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const setTheme = useCallback((theme: Theme) => setState((s) => ({ ...s, theme })), []);
  const setContrast = useCallback((contrast: Contrast) => setState((s) => ({ ...s, contrast })), []);
  const setFontSize = useCallback((fontSize: FontSize) => setState((s) => ({ ...s, fontSize })), []);
  const setReducedMotion = useCallback((reducedMotion: boolean) => setState((s) => ({ ...s, reducedMotion })), []);
  const setSpacing = useCallback((spacing: Spacing) => setState((s) => ({ ...s, spacing })), []);
  const setLinkHighlight = useCallback((linkHighlight: boolean) => setState((s) => ({ ...s, linkHighlight })), []);
  const setReadingGuide = useCallback((readingGuide: boolean) => setState((s) => ({ ...s, readingGuide })), []);

  const resetAll = useCallback(() => {
    setState(defaults);
    const keys = [
      'a11y-theme',
      'a11y-contrast',
      'a11y-font-size',
      'a11y-reduced-motion',
      'a11y-spacing',
      'a11y-link-highlight',
      'a11y-reading-guide',
    ];
    keys.forEach((k) => localStorage.removeItem(k));
    // Clean up DOM attributes
    const h = document.documentElement;
    h.removeAttribute('data-theme');
    h.removeAttribute('data-contrast');
    h.removeAttribute('data-font-size');
    h.removeAttribute('data-spacing');
    h.removeAttribute('data-reduced-motion');
    h.removeAttribute('data-link-highlight');
  }, []);

  const value = useMemo<AccessibilityContextValue>(
    () => ({
      ...state,
      setTheme,
      setContrast,
      setFontSize,
      setReducedMotion,
      setSpacing,
      setLinkHighlight,
      setReadingGuide,
      resetAll,
    }),
    [
      state,
      setTheme,
      setContrast,
      setFontSize,
      setReducedMotion,
      setSpacing,
      setLinkHighlight,
      setReadingGuide,
      resetAll,
    ]
  );

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return ctx;
}
